import { DictionaryEntry } from '@sdbn/common';
import { MasterDetailListEntry } from '@sdbn/common';

export class ConditionType {
  nameDictionaryKey: string;
  type: string;
  defaultData?: any;

  constructor(type: ConditionType) {
    this.nameDictionaryKey = type.nameDictionaryKey;
    this.type = type.type;
    this.defaultData = type.defaultData;
  }
}

export class ConditionGroup {
  nameDictionaryKey: string;
  types = new Array<ConditionType>();

  constructor(group: ConditionGroup) {
    this.nameDictionaryKey = group.nameDictionaryKey;
    for (const type of group.types) {
      this.types.push(new ConditionType(type));
    }
  }
}

export enum LogicalOperator {
  AND = 'AND', OR = 'OR'
}

export class FilterCondition {

  id: number;

  deleted?: boolean;
  // Zu persistieren
  type: string;
  // Lesbarer String für Anzeige
  dataString?: string;
  data: any = {};

  constructor(condition: FilterCondition) {
    this.id = condition.id;
    this.deleted = condition.deleted || false;
    this.type = condition.type;
    this.dataString = condition.dataString;
    this.data = condition.data || {};
  }
}

export enum FilterType {
  TRANSPORT_LIST = 'TRANSPORT_LIST', VISITOR_LIST = 'VISITOR_LIST'
}

export class EditorFilter {

  dirty?: boolean;
  valid?: boolean;

  id: number;
  filterType = FilterType.TRANSPORT_LIST;
  tableColumns?: string;
  sortColumn?: string;
  sortDescending?: boolean;
  rootNode: EditorFilterTreeNode;
  displayOnly?: boolean;
  name = new DictionaryEntry();
  sortOrder: string;
  filterMailSchedules = new Array<FilterMailSchedule>();

  constructor(filter: EditorFilter) {
    this.id = filter.id;
    this.filterType = filter.filterType;
    this.tableColumns = filter.tableColumns || '';
    this.sortColumn = filter.sortColumn;
    this.sortDescending = filter.sortDescending;
    this.displayOnly = filter.displayOnly;
    this.name = new DictionaryEntry(filter.name);
    this.sortOrder = filter.sortOrder;

    if (filter.rootNode != null) {
      const node = new EditorFilterTreeNode(filter.rootNode);
      node.filterId = this.id;
      this.rootNode = node;
    }

    if (filter.filterMailSchedules != null) {
      for (const schedule of filter.filterMailSchedules) {
        this.filterMailSchedules.push(new FilterMailSchedule(schedule))
      }
    }
  }
}

export class EditorFilterTreeNode {

  id: number;
  filterId?: number;
  operator: LogicalOperator;
  conditions = new Array<FilterCondition>();
  deleted?: boolean;
  parentNode?: EditorFilterTreeNode;
  nodes = new Array<EditorFilterTreeNode>();

  constructor(node: EditorFilterTreeNode) {
    if (node != null) {
      this.id = node.id;
      this.filterId = node.filterId;
      this.operator = node.operator;
      this.deleted = node.deleted || false;
      this.parentNode = node.parentNode;

      for (const condition of node.conditions) {
        this.conditions.push(new FilterCondition(condition));
      }

      for (const child of node.nodes) {
        const childNode = new EditorFilterTreeNode(child);
        // set reference to parent node
        childNode.parentNode = this;
        childNode.filterId = this.filterId;
        this.nodes.push(childNode);
      }
    }
  }
}

export class Filter {

  id: number;
  filterType = FilterType.TRANSPORT_LIST;
  tableColumns?: string;
  sortColumn?: string;
  sortDescending?: boolean;
  displayOnly?: boolean;
  rootNode: FilterTreeNode;
  name = new DictionaryEntry();
  sortOrder: string;
  filterMailSchedules = new Array<FilterMailSchedule>();

  constructor(filter: EditorFilter) {
    this.id = filter.id;
    this.filterType = filter.filterType;
    this.sortDescending = filter.sortDescending || false;
    this.tableColumns = filter.tableColumns || '';
    this.sortColumn = filter.sortColumn;
    this.displayOnly = filter.displayOnly;
    this.name = new DictionaryEntry(filter.name);
    this.sortOrder = filter.sortOrder;
    if (filter.rootNode != null) {
      this.rootNode = new FilterTreeNode(filter.rootNode);
    }
    if (filter.filterMailSchedules != null) {
      for (const schedule of filter.filterMailSchedules) {
        this.filterMailSchedules.push(new FilterMailSchedule(schedule))
      }
    }
  }
}

export class FilterTreeNode {

  id: number;
  filterId?: number;
  parentId?: number;
  operator: LogicalOperator;
  conditions = new Array<FilterCondition>();
  deleted?: boolean;
  nodes = new Array<EditorFilterTreeNode>();

  constructor(node: FilterTreeNode) {
    this.id = node.id;
    this.filterId = node.filterId;
    this.operator = node.operator;
    this.deleted = node.deleted || false;

    for (const condition of node.conditions) {
      this.conditions.push(new FilterCondition(condition));
    }

    for (const child of node.nodes) {
      const childNode = new FilterTreeNode(child);
      childNode.parentId = this.id;
      this.nodes.push(childNode);
    }
  }
}

export class PreviewFilter implements MasterDetailListEntry {
  id: number;
  sortOrder: string;
  nameDictionaryKey: string;
  translatedName: string;

  constructor(preview: Partial<PreviewFilter>) {
    this.id = preview.id;
    this.sortOrder = preview.sortOrder;
    this.nameDictionaryKey = preview.nameDictionaryKey;
  }

  public static getDefault(parentId: number): PreviewFilter {
    return new PreviewFilter({});
  }

  public static getDeserialized(preview: Partial<PreviewFilter>): PreviewFilter {
    return new PreviewFilter(preview);
  }

  public get listTitle(): string {
    return this.translatedName;
  }

  public get listTitleDictionaryKey(): string {
    return null;
  }

  public get listSubTitle(): string {
    return null;
  }

  public get groupIdentifier(): String {
    return null;
  }

  public get childs(): Array<MasterDetailListEntry> {
    return null;
  }
}

export class FilterSortOrder {
  id: number;
  sortOrder: string;

  constructor(preview: PreviewFilter) {
    this.id = preview.id;
    this.sortOrder = preview.sortOrder;
  }
}

export class FilterMailSchedule implements MasterDetailListEntry {

  title: string;

  id: number;
  recipients: String;
  timeOfDay: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
  exportType: boolean;
  periodic: boolean;
  periodicInterval: boolean;
  deleted = false;

  constructor(schedule: Partial<FilterMailSchedule>) {
    if (schedule != null) {
      this.id = schedule.id;
      this.recipients = schedule.recipients;
      this.timeOfDay = schedule.timeOfDay;
      this.monday = schedule.monday;
      this.tuesday = schedule.tuesday;
      this.wednesday = schedule.wednesday;
      this.thursday = schedule.thursday;
      this.friday = schedule.friday;
      this.saturday = schedule.saturday;
      this.sunday = schedule.sunday;
      this.exportType = schedule.exportType;
      this.periodic = schedule.periodic;
      this.periodicInterval = schedule.periodicInterval;
      this.deleted = schedule.deleted;
    }
  }

  public get listTitle(): string {
    return this.title;
  }

  public get listTitleDictionaryKey(): string {
    return null;
  }

  public get listSubTitle(): string {
    return null;
  }

  public get groupIdentifier(): String {
    return null;
  }

  public get childs(): Array<MasterDetailListEntry> {
    return null;
  }

  get isValid(): boolean {
    return (this.periodic && this.periodicInterval != null) || (!this.periodic && this.timeOfDay != null && this.recipients != null && (this.monday || this.tuesday || this.wednesday || this.thursday || this.friday || this.saturday || this.sunday));
  }
}
